import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IDocumentBase } from 'app/services/api5-service/api.interface';
import { BehaviorSubject, OperatorFunction, pipe, Subject } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { HttpResponseHandler } from './model';

/**
 * Http handler service for handling doc features change in order to reload page if needed
 */
@Injectable({
  providedIn: 'root',
})
export class FeaturesHandlerService implements HttpResponseHandler {
  /** Doc property $features subject */
  private readonly docFeaturesSubject = new Subject<string>();

  /** Number of requests currently executing subject */
  private readonly activeRequestsCountSubject = new BehaviorSubject<number>(0);

  /** Doc features change event */
  readonly docFeaturesChange$ = this.docFeaturesSubject.pipe(
    filter(features => Boolean(features)),
    distinctUntilChanged(),
    // skip first feature - wait for distinctUntilChanged
    skip(1),
    // if need to reboot the whole interface,
    // make sure that all running requests to the server are completed
    switchMap(() =>
      this.activeRequestsCountSubject.pipe(
        filter(requestsCount => requestsCount === 0),
        take(1),
      ),
    ),
    // asyncSheduler (setTimeout) for time to handle all response, like close form
    delay(0),
  );

  onStart(): void {
    // register amount of requests to the server
    // (new request to the server is initiated - the count of requests increases,
    //  request completes - the count of requests decreases)
    this.activeRequestsCountSubject.next(
      this.activeRequestsCountSubject.value + 1,
    );
  }

  /**
   * Update doc features and check the need for reloading page
   */
  handle(): OperatorFunction<IDocumentBase, IDocumentBase> {
    return pipe(
      tap(doc => {
        if (doc.$features && doc.$stylesheet !== 'login') {
          this.docFeaturesSubject.next(doc.$features);
        }
      }),
      finalize(() =>
        this.activeRequestsCountSubject.next(
          this.activeRequestsCountSubject.value - 1,
        ),
      ),
    );
  }

  handleEvent(): OperatorFunction<
    HttpEvent<IDocumentBase>,
    HttpEvent<IDocumentBase>
  > {
    // @TODO i.ablov update features on file loading
    return pipe(
      finalize(() =>
        this.activeRequestsCountSubject.next(
          this.activeRequestsCountSubject.value - 1,
        ),
      ),
    );
  }
}
